import { INLINES } from '@contentful/rich-text-types';
import cls from 'classnames';
import { FC } from 'react';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { useSelector } from 'react-redux';

import newsletterImage from '@/assets/form-images/form-image.svg';
import NormalButton from '@/components/button/normal';
import Input from '@/components/form-elements/input';
import Image from '@/components/image';
import RichTextRenderer, {
  renderInlineHyperlink,
} from '@/components/rich-text-renderer';
import getThemedButtonProps from '@/lib/get-themed-button-props';
import getVariablePlainText from '@/lib/get-variable-plain-text';
import useBreakpoint from '@/lib/hooks/use-breakpoint';
import useVariables from '@/lib/hooks/use-variables';
import { selectGlobalPageTheme } from '@/store/slices/global';
import { ImageAsset } from '@/types/views/generic';
import { NewsletterStaticVariant } from '@/types/views/sections';

import getVariantVariableKey from '../../lib/get-variant-variable-key';
import { NewsletterFormInputData } from '../../lib/newsletter-form-data';
import styles, { eventStyles } from './styles';

const EMAIL_REGEX_PATTERN =
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/g;

type FirstStepProps = {
  register: UseFormRegister<NewsletterFormInputData>;
  errors: FieldErrors;
  buttonDisabled: boolean;
  onNextButtonClick: () => void;
  newsletterMobileImage?: ImageAsset;
  newsletterDesktopImage?: ImageAsset;
  newsletterVariant: NewsletterStaticVariant;
  sectionTitle?: string;
};

const FirstStep: FC<FirstStepProps> = ({
  register,
  errors,
  buttonDisabled,
  onNextButtonClick,
  newsletterMobileImage,
  newsletterDesktopImage,
  newsletterVariant,
}) => {
  const variables = useVariables();
  const pageTheme = useSelector(selectGlobalPageTheme);
  const buttonProps = getThemedButtonProps(pageTheme);
  const isEsnTheme = pageTheme === 'esn';
  const isElsTheme = pageTheme === 'els';
  const variantKey = (suffix: string) =>
    getVariantVariableKey(newsletterVariant, suffix);
  const isEventVariant = newsletterVariant === 'event';

  const signupText =
    variables[variantKey(isEsnTheme ? 'signup-text-esn' : 'signup-text')];
  const signupPretext = isEventVariant
    ? variables[variantKey('signup-pretext')]
    : undefined;
  const signupPosttext = isEventVariant
    ? variables[variantKey('signup-posttext')]
    : undefined;
  const emailPlaceholderText = getVariablePlainText(
    variables[variantKey('signup-email-text')],
  );
  const emailButtonText = getVariablePlainText(
    variables[variantKey('signup-cta-text')],
  );
  const emailErrorText = getVariablePlainText(variables['email-error-text']);
  const invalidEmailText = getVariablePlainText(
    variables['invalid-email-text'],
  );

  const { isMobile } = useBreakpoint();
  const staticNewsletterImage = isMobile
    ? newsletterMobileImage?.src
    : newsletterDesktopImage?.src;

  return (
    <div className={cls(isEventVariant ? eventStyles.wrapper : styles.wrapper)}>
      {!isElsTheme && (
        <figure
          className={
            isEventVariant ? eventStyles.imageContainer : styles.imageContainer
          }
        >
          <Image
            src={staticNewsletterImage || newsletterImage.src}
            alt={isEventVariant ? 'Event registration' : 'Save to newsletter'}
            objectFit="contain"
          />
        </figure>
      )}

      <div className={isEventVariant ? eventStyles.leftSide : styles.leftSide}>
        {signupPretext && (
          <RichTextRenderer className={styles.pretext} body={signupPretext} />
        )}

        <RichTextRenderer
          className={styles.heading}
          body={signupText}
          options={{
            renderNode: {
              [INLINES.HYPERLINK]: renderInlineHyperlink('text-h4'),
            },
          }}
        />

        {signupPosttext && (
          <RichTextRenderer className={styles.posttext} body={signupPosttext} />
        )}

        <div
          className={isEventVariant ? eventStyles.rowGroup : styles.rowGroup}
        >
          <Input
            {...register('email', {
              required: emailErrorText || 'Email is required',
              pattern: {
                value: EMAIL_REGEX_PATTERN,
                message: invalidEmailText || 'Please enter a valid email.',
              },
            })}
            id="email"
            label={emailPlaceholderText || 'Email Address'}
            type="email"
            placeholder={emailPlaceholderText || 'Email Address'}
            containerClassName={isEventVariant ? 'lg:mr-4' : 'md:mr-4'}
            className={isEventVariant ? eventStyles.input : styles.input}
            error={errors?.email?.message}
          />

          <NormalButton
            {...buttonProps}
            text={emailButtonText}
            size="L"
            className={isEventVariant ? eventStyles.button : styles.button}
            onClick={onNextButtonClick}
            disabled={buttonDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export default FirstStep;
